import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  DollarSign,
  User,
} from "lucide-react";
import { Booking, getBookings } from "../../services/BookingsApi";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

export default function BookingsSummaryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      // Fetch a large number to get accurate summary stats
      const result = await getBookings({ per_page: 500 });
      setBookings(result.bookings || []);
    } catch (error) {
      console.error("Failed to fetch bookings", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Statistics & Derived Data ---
  const stats = useMemo(() => {
    const total = bookings.length;
    const completed = bookings.filter((b) => b.status === "Completed").length;
    const pending = bookings.filter(
      (b) => b.status === "Pending" || b.status === "Booked"
    ).length;
    const cancelled = bookings.filter(
      (b) => b.status === "Cancelled" || b.status === "No Show"
    ).length;

    const totalRevenue = bookings.reduce(
      (sum, b) =>
        sum +
        (typeof b.total === "number"
          ? b.total
          : parseFloat(String(b.total || 0))),
      0
    );

    return { total, completed, pending, cancelled, totalRevenue };
  }, [bookings]);

  // Chart Data: Status Distribution
  const statusData = useMemo(
    () =>
      [
        { name: "Completed", value: stats.completed, color: "#885ABB" },
        { name: "Pending", value: stats.pending, color: "#C084FC" },
        { name: "Cancelled", value: stats.cancelled, color: "#E9D5FF" },
      ].filter((d) => d.value > 0),
    [stats]
  );

  // Chart Data: Bookings by Staff (Top 5)
  const staffData = useMemo(() => {
    const counts: Record<string, number> = {};
    bookings.forEach((b) => {
      const staffName = b.employeeName;
      if (staffName) {
        counts[staffName] = (counts[staffName] || 0) + 1;
      }
    });

    return Object.entries(counts)
      .map(([name, value]) => ({
        name: name.split(" ")[0],
        fullName: name,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [bookings]);

  const recentBookings = useMemo(() => {
    return [...bookings]
      .sort(
        (a, b) =>
          new Date(b.bookingTime).getTime() - new Date(a.bookingTime).getTime()
      )
      .slice(0, 5);
  }, [bookings]);

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <GlobalLoader description="Loading bookings summary..." />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Bookings Summary"
        description="Overview of appointments and booking performance"
        icon={Calendar}
        variant="default"
        className="mb-0"
      />

      <div className="space-y-6 pt-4">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total Bookings"
            value={stats.total}
            icon={Calendar}
            trend="+12 this week"
            trendUp={true}
          />
          <MetricCard
            label="Completed"
            value={stats.completed}
            icon={CheckCircle}
            subValue={`${Math.round(
              (stats.completed / (stats.total || 1)) * 100
            )}% completion`}
          />
          <MetricCard
            label="Pending / Upcoming"
            value={stats.pending}
            icon={Clock}
          />
          <MetricCard
            label="Est. Revenue"
            value={`$${stats.totalRevenue.toLocaleString()}`}
            icon={DollarSign}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Staff Performance Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-6">
              Bookings by Staff
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={staffData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "#F9FAFB" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#885ABB"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Pie Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-4">
              Booking Status
            </h3>
            <div className="h-[200px] w-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={0}
                    dataKey="value"
                    stroke="none"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-gray-900">
                  {stats.total}
                </span>
                <span className="text-xs text-gray-500 font-medium">Total</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-4">
              {statusData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Bookings Table */}
        <div className="pb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#402B69]">
                Recent Bookings
              </h3>
              <Link
                to="/bookings"
                className="text-sm text-[#885ABB] font-medium hover:text-[#6f47a2] transition-colors"
              >
                View All
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#885ABB] text-white font-medium">
                  <tr>
                    <th className="px-6 py-3 font-semibold">Client</th>
                    <th className="px-6 py-3 font-semibold">Staff</th>
                    <th className="px-6 py-3 font-semibold">Date & Time</th>
                    <th className="px-6 py-3 font-semibold text-center">
                      Status
                    </th>
                    <th className="px-6 py-3 font-semibold text-right">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      className="group hover:bg-gray-50/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/summary/bookings/${booking.id}`)}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {booking.customerName}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {booking.employeeName ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                              {booking.employeeName.charAt(0)}
                            </div>
                            <span className="truncate max-w-[120px]">{booking.employeeName}</span>
                          </div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {booking.bookingTime}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <StatusBadge status={booking.status} />
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        $
                        {typeof booking.total === "number"
                          ? booking.total.toFixed(2)
                          : booking.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  subValue,
  trend,
  trendUp,
}: any) {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-[#885ABB]" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${trendUp ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }`}
          >
            {trend}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-1">{label}</h3>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {subValue && (
            <span className="text-xs text-gray-400 truncate max-w-[120px]">
              {subValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    Completed: "bg-green-50 text-green-700 border-green-100",
    Pending: "bg-yellow-50 text-yellow-700 border-yellow-100",
    Booked: "bg-blue-50 text-blue-700 border-blue-100",
    Cancelled: "bg-red-50 text-red-700 border-red-100",
    "No Show": "bg-gray-50 text-gray-700 border-gray-100",
  };

  const defaultStyle = "bg-gray-50 text-gray-700 border-gray-100";

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || defaultStyle
        }`}
    >
      {status}
    </span>
  );
}
